import {useEffect, useRef, useCallback} from 'react';
import {PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {getDefaultAttachments} from '@Components/ai-chat-window/lib/ai-chat-prompt.library';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {nextPromptBoxKey} from '@Components/ai-prompt-box/libraries/ai-prompt-box-key.library';

interface UseAiChatPromptBoxCoordinatorProps {
  promptBoxId: string;
  chatId: string | undefined;
  isMessageInProgress: boolean;
}

export interface PromptBoxCoordinatorActions {
  activateForNewChat: () => void;
  activatePromptBox: () => void;
}

const MOBILE_WIDTH_BREAKPOINT = 992;

export function useAiChatPromptBoxCoordinator({
  promptBoxId,
  chatId,
  isMessageInProgress,
}: UseAiChatPromptBoxCoordinatorProps): PromptBoxCoordinatorActions {
  const {actions: aiPromptBoxActions} = useAiPromptBox(promptBoxId);
  const previousChatIdRef = useRef<string | undefined>(chatId);
  const pendingIntentRef = useRef<'new-chat' | null>(null);
  const prevIsMessageInProgressRef = useRef(isMessageInProgress);

  // Effect: Chat switching
  useEffect(() => {
    const previousChatId = previousChatIdRef.current;
    const currentChatId = chatId;

    if (currentChatId) {
      aiPromptBoxActions.update({
        showDisclaimer: true,
        promptBoxState: PromptBoxState.IDLE,
      });
    } else if (previousChatId !== undefined) {
      // Navigating from an existing chat to new chat screen.
      // On initial mount with no chatId, skip the reset so pre-filled state
      // (e.g. from chatOnLoadParams GET params) is preserved.
      const isPendingNewChat = pendingIntentRef.current === 'new-chat';
      pendingIntentRef.current = null;

      aiPromptBoxActions.update({
        prompt: '',
        attachments: getDefaultAttachments(),
        pinnedAspectRatio: null,
        pinnedBrandKitId: null,
        showDisclaimer: false,
        ...(isPendingNewChat ? {promptBoxState: PromptBoxState.ACTIVE, focusKey: nextPromptBoxKey()} : {}),
      });
    }

    previousChatIdRef.current = currentChatId;
  }, [chatId]);

  // Effect: Streaming lifecycle
  useDidUpdateEffect(() => {
    const wasMessageInProgress = prevIsMessageInProgressRef.current;
    prevIsMessageInProgressRef.current = isMessageInProgress;

    if (isMessageInProgress) {
      aiPromptBoxActions.update({
        promptBoxState: PromptBoxState.STOPPABLE_LOADING,
        placeholder: window.i18next.t('pmwjs_make_chaneges_or_create_something_new_ellipses'),
        prompt: '',
        attachments: {items: []},
      });
      return;
    }

    if (wasMessageInProgress) {
      const shouldAutoActivate = window.innerWidth > MOBILE_WIDTH_BREAKPOINT;
      aiPromptBoxActions.update({
        promptBoxState: shouldAutoActivate ? PromptBoxState.ACTIVE : PromptBoxState.IDLE,
        placeholder: window.i18next.t('pmwjs_make_chaneges_or_create_something_new_ellipses'),
      });
    }
  }, [isMessageInProgress, chatId]);

  const activateForNewChat = useCallback((): void => {
    if (previousChatIdRef.current === undefined) {
      // Already on new chat screen — directly activate without waiting for effect
      aiPromptBoxActions.update({
        promptBoxState: PromptBoxState.ACTIVE,
        focusKey: nextPromptBoxKey(),
      });
      return;
    }

    pendingIntentRef.current = 'new-chat';
  }, [aiPromptBoxActions]);

  const activatePromptBox = useCallback((): void => {
    aiPromptBoxActions.update({
      promptBoxState: PromptBoxState.ACTIVE,
    });
  }, [aiPromptBoxActions]);

  return {activateForNewChat, activatePromptBox};
}
